import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  useToast,
  VStack,
  Text,
  Box,
  Divider,
  Flex,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Select,
  ButtonGroup,
} from '@chakra-ui/react';
import { DownloadIcon } from '@chakra-ui/icons';
import { getPayrollDetails, updatePayrollStatus, downloadPayrollPdf } from 'services/payroll.service';

export default function PayrollDetailModal({
  isOpen,
  onClose,
  payroll,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState([]);
  const toast = useToast();
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  useEffect(() => {
    const loadDetails = async () => {
      if (!isOpen || !payroll?.id) return;

      setDetailsLoading(true);
      try {
        const response = await getPayrollDetails(payroll.id);
        const loadedDetails = response?.payload?.details || [];
        setDetails(loadedDetails);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'No se pudo cargar el detalle de conceptos.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setDetailsLoading(false);
      }
    };

    loadDetails();
  }, [isOpen, payroll?.id, toast]);

  const handleStatusChange = async (newStatus) => {
    try {
      setLoading(true);
      const response = await updatePayrollStatus(payroll.id, {
        status: newStatus,
      });

      if (response.status === 200) {
        toast({
          title: 'Éxito',
          description: 'Estado actualizado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        onSuccess();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el estado',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'gray';
      case 'CALCULATED':
        return 'yellow';
      case 'APPROVED':
        return 'green';
      case 'PAID':
        return 'purple';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'DRAFT':
        return 'Borrador';
      case 'CALCULATED':
        return 'Calculado';
      case 'APPROVED':
        return 'Aprobado';
      case 'PAID':
        return 'Pagado';
      default:
        return status;
    }
  };

  const handleGeneratePDF = async () => {
    if (!payroll?.id) return;
    try {
      const blob = await downloadPayrollPdf(payroll.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast({
        title: 'Error',
        description: 'No se pudo descargar el PDF.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Adaptar nombres de campos del backend
  const employeeName = payroll.employee?.person?.name && payroll.employee?.person?.lastName
    ? `${payroll.employee.person.name} ${payroll.employee.person.lastName}`
    : payroll.employee_name || 'Sin nombre';
  
  const employeeDocument = payroll.employee?.person?.document || payroll.employee_document || '-';

  const deductions = [
    {
      label: 'IRPF',
      amount: payroll.irpf || 0,
      percentage: payroll.gross_salary
        ? (((payroll.irpf || 0) / payroll.gross_salary) * 100).toFixed(2)
        : 0,
      color: 'red.500',
    },
    {
      label: 'BPS',
      amount: payroll.bps || 0,
      percentage: payroll.gross_salary
        ? (((payroll.bps || 0) / payroll.gross_salary) * 100).toFixed(2)
        : 0,
      color: 'orange.500',
    },
    {
      label: 'FONASA',
      amount: payroll.fonasa || 0,
      percentage: payroll.gross_salary
        ? (((payroll.fonasa || 0) / payroll.gross_salary) * 100).toFixed(2)
        : 0,
      color: 'blue.500',
    },
  ];

  const totalDeductions = payroll.total_deductions || 
    ((payroll.irpf || 0) + (payroll.bps || 0) + (payroll.fonasa || 0));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex justify="space-between" align="center">
            <Text>Detalle de Liquidación</Text>
            <Badge colorScheme={getStatusColor(payroll.status)}>
              {getStatusLabel(payroll.status)}
            </Badge>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Información del empleado */}
            <Box>
              <Text fontSize="sm" color="gray.500">
                Empleado
              </Text>
              <Text fontSize="lg" fontWeight="600">
                {employeeName}
              </Text>
              <Text fontSize="sm" color="gray.500">
                Documento: {employeeDocument}
              </Text>
              <Text fontSize="sm" color="gray.500">
                Días trabajados: {payroll.worked_days || 30}
              </Text>
            </Box>

            <Divider />

            {/* Sueldo Bruto */}
            <Flex justify="space-between" align="center">
              <Text fontSize="md" fontWeight="500">
                Sueldo Bruto
              </Text>
              <Text fontSize="xl" fontWeight="600">
                ${' '}
                {(payroll.gross_salary || 0).toLocaleString('es-UY', {
                  minimumFractionDigits: 2,
                })}
              </Text>
            </Flex>

            {(payroll.bonuses || 0) > 0 && (
              <Flex justify="space-between" align="center">
                <Text fontSize="md" color="gray.600">
                  + Bonificaciones
                </Text>
                <Text fontSize="md" color="green.500">
                  ${' '}
                  {(payroll.bonuses || 0).toLocaleString('es-UY', {
                    minimumFractionDigits: 2,
                  })}
                </Text>
              </Flex>
            )}

            {(payroll.overtime_amount_50 || 0) > 0 && (
              <Flex justify="space-between" align="center">
                <Text fontSize="md" color="gray.600">
                  + Horas Extra 50% ({payroll.overtime_hours_50 || 0} hs)
                </Text>
                <Text fontSize="md" color="green.500">
                  ${' '}
                  {(payroll.overtime_amount_50 || 0).toLocaleString('es-UY', {
                    minimumFractionDigits: 2,
                  })}
                </Text>
              </Flex>
            )}

            {(payroll.overtime_amount_100 || 0) > 0 && (
              <Flex justify="space-between" align="center">
                <Text fontSize="md" color="gray.600">
                  + Horas Extra 100% ({payroll.overtime_hours_100 || 0} hs)
                </Text>
                <Text fontSize="md" color="green.500">
                  ${' '}
                  {(payroll.overtime_amount_100 || 0).toLocaleString('es-UY', {
                    minimumFractionDigits: 2,
                  })}
                </Text>
              </Flex>
            )}

            <Divider />

            {/* Deducciones */}
            <Box>
              <Text fontSize="md" fontWeight="500" mb={2}>
                Deducciones
              </Text>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor}>Concepto</Th>
                    <Th borderColor={borderColor} isNumeric>
                      %
                    </Th>
                    <Th borderColor={borderColor} isNumeric>
                      Monto
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {deductions.map((deduction, index) => (
                    <Tr key={index}>
                      <Td borderColor={borderColor}>
                        <Text fontWeight="500" color={deduction.color}>
                          {deduction.label}
                        </Text>
                      </Td>
                      <Td borderColor={borderColor} isNumeric>
                        {deduction.percentage}%
                      </Td>
                      <Td borderColor={borderColor} isNumeric>
                        <Text color={deduction.color}>
                          ${' '}
                          {deduction.amount.toLocaleString('es-UY', {
                            minimumFractionDigits: 2,
                          })}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                  <Tr>
                    <Td borderColor={borderColor}>
                      <Text fontWeight="600">Total Deducciones</Text>
                    </Td>
                    <Td borderColor={borderColor}></Td>
                    <Td borderColor={borderColor} isNumeric>
                      <Text fontWeight="600">
                        ${' '}
                        {totalDeductions.toLocaleString('es-UY', {
                          minimumFractionDigits: 2,
                        })}
                      </Text>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>

            <Divider />

            {/* Sueldo Líquido */}
            <Box
              p={4}
              bg="green.50"
              borderRadius="md"
              borderWidth="2px"
              borderColor="green.200"
            >
              <Flex justify="space-between" align="center">
                <Text fontSize="lg" fontWeight="600">
                  Sueldo Líquido a Pagar
                </Text>
                <Text fontSize="2xl" fontWeight="700" color="green.600">
                  ${' '}
                  {(payroll.net_salary || 0).toLocaleString('es-UY', {
                    minimumFractionDigits: 2,
                  })}
                </Text>
              </Flex>
            </Box>

            <Divider />

            {/* Detalle por conceptos */}
            <Box>
              <Text fontSize="md" fontWeight="500" mb={2}>
                Detalle por concepto
              </Text>
              {detailsLoading ? (
                <Text fontSize="sm" color="gray.500">
                  Cargando detalle...
                </Text>
              ) : (
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th borderColor={borderColor}>Concepto</Th>
                      <Th borderColor={borderColor}>Tipo</Th>
                      <Th borderColor={borderColor} isNumeric>Cant.</Th>
                      <Th borderColor={borderColor} isNumeric>Unit.</Th>
                      <Th borderColor={borderColor} isNumeric>Total</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(details || []).map((d) => (
                      <Tr key={d.id}>
                        <Td borderColor={borderColor}>
                          {d.concept_name || d.concept_code || '—'}
                          {d.is_manual ? ' (Manual)' : ''}
                        </Td>
                        <Td borderColor={borderColor}>{d.concept_type || '—'}</Td>
                        <Td borderColor={borderColor} isNumeric>
                          {Number(d.quantity ?? 0).toFixed(2)}
                        </Td>
                        <Td borderColor={borderColor} isNumeric>
                          {(d.unit_amount ?? 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Td>
                        <Td borderColor={borderColor} isNumeric>
                          {(d.total_amount ?? 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Td>
                      </Tr>
                    ))}
                    {(details || []).length === 0 && (
                      <Tr>
                        <Td borderColor={borderColor} colSpan={5} textAlign="center">
                          Sin conceptos
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              )}
            </Box>

            <Divider />

            {/* Cambiar estado */}
            <Box>
              <Text fontSize="sm" fontWeight="500" mb={2}>
                Cambiar Estado
              </Text>
              <Select
                value={payroll.status}
                onChange={(e) => handleStatusChange(e.target.value)}
                isDisabled={loading}
              >
                <option value="DRAFT">Borrador</option>
                <option value="CALCULATED">Calculado</option>
                <option value="APPROVED">Aprobado</option>
                <option value="PAID">Pagado</option>
                <option value="CANCELLED">Cancelado</option>
              </Select>
            </Box>

            {payroll.notes && (
              <Box>
                <Text fontSize="sm" fontWeight="500" mb={2}>
                  Observaciones
                </Text>
                <Text fontSize="sm" color="gray.600">
                  {payroll.notes}
                </Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <ButtonGroup spacing={3}>
            <Button
              leftIcon={<DownloadIcon />}
              colorScheme="blue"
              variant="outline"
              onClick={handleGeneratePDF}
            >
              Descargar PDF
            </Button>
            <Button onClick={onClose}>Cerrar</Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
